import type { ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import type { Schemas } from "../../workspace/api.generated";
import {
  bankName,
  businessCodeLabel,
  date,
  money,
} from "../../workspace/model";
import {
  Kpi,
  PageHeader,
  Panel,
  RowAction,
  RowLink,
  statusBadge,
} from "../../workspace/shared";
import { transferSearchFormSchema } from "../schema";
import type { TransferSearchFormValues } from "../schema";

type Transfer = Schemas["TransferAdminView"];
export interface TransferListViewProps {
  readonly funding?: ReactNode;
  readonly onExport?: () => void;
  readonly filters: TransferSearchFormValues;
  readonly result?: Schemas["AdminBoPageTransferAdminView"];
  readonly state?: "ready" | "loading" | "error";
  readonly refreshing?: boolean;
  readonly notice?: string;
  readonly detailHref?: (id: number) => string;
  readonly onSearch: (filters: TransferSearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onRetry: () => void;
  readonly onViewTransfer: (id: number) => void;
  readonly onCancel?: (row: Transfer) => void;
  readonly onRetryTransfer?: (row: Transfer) => void;
}
const labels: Readonly<Record<string, string>> = {
  READY: "송금 대기",
  COMPLETED: "송금 완료",
  FAILED: "송금 실패",
  CANCELED: "송금 취소",
  PROCESSING: "처리 중",
  UNKNOWN: "결과 확인 중",
};
export function TransferListView({
  onExport,
  funding,
  filters,
  result,
  state = "ready",
  refreshing = false,
  notice,
  detailHref,
  onSearch,
  onPageChange,
  onRetry,
  onViewTransfer,
  onCancel,
  onRetryTransfer,
}: TransferListViewProps) {
  const form = useForm<TransferSearchFormValues>({
    resolver: zodResolver(transferSearchFormSchema),
    values: filters,
    shouldFocusError: true,
  });
  const action = (
    row: Transfer,
    code: string,
    callback: ((row: Transfer) => void) | undefined,
    label: string,
  ) => {
    const rule = row.availableActions?.find((a) => a.code === code);
    const forbidden =
      code === "TRANSFER_RETRY" && row.lastErrorCode === "ADMIN_CANCEL";
    return (
      <>
        <Button
          variant="quiet"
          size="small"
          disabled={!rule?.allowed || !callback || forbidden}
          onClick={() => callback?.(row)}
        >
          {label}
        </Button>

      </>
    );
  };
  const columns: readonly DataColumn<Transfer>[] = [
    {
      key: "transaction",
      title: "거래ID",
      sticky: "start",
      render: (r) =>
        detailHref ? (
          <RowLink
            href={detailHref(r.id)}
            onNavigate={() => onViewTransfer(r.id)}
            triggerKey={`transfer:${r.id}:detail`}
          >
            {r.displayTransactionNo ?? "정보 없음"}
          </RowLink>
        ) : (
          <RowAction onClick={() => onViewTransfer(r.id)}>
            {r.displayTransactionNo ?? "정보 없음"}
          </RowAction>
        ),
    },
    {
      key: "date",
      title: "송금 일시",
      render: (r) => (
        <>
          {date(r.occurredAt)}
          {r.status === "READY" ? (
            <small> · 예정</small>
          ) : r.status === "COMPLETED" ? (
            <small> · 완료</small>
          ) : null}
        </>
      ),
    },
    {
      key: "name",
      title: "회원명",
      render: (r) => r.memberName ?? "정보 없음",
    },
    {
      key: "phone",
      title: "휴대폰번호",
      render: (r) => r.phoneNumber ?? "정보 없음",
    },
    {
      key: "amount",
      title: "금액",
      numeric: true,
      render: (r) =>
        money(
          r.status === "COMPLETED" ? r.transferredAmount : r.requestedAmount,
        ),
    },
    {
      key: "account",
      title: "입금 계좌",
      render: (r) =>
        `${r.bankName ?? bankName(r.bankCode)} · ${r.accountNumberMasked}`,
    },
    { key: "holder", title: "예금주", render: (r) => r.accountHolderMasked },
    {
      key: "status",
      title: "상태",
      render: (r) => (
        <>
          {labels[r.displayStatus ?? r.status] ?? statusBadge(r.status)}
          {r.lastErrorCode ? (
            <small>
              {" "}
              ·{" "}
              {r.lastErrorCode === "ADMIN_CANCEL"
                ? "관리자 취소"
                : businessCodeLabel(r.lastErrorCode)}
            </small>
          ) : null}
        </>
      ),
    },
    { key: "number", title: "송금ID", render: (r) => r.transferNo },
    {
      key: "cancel",
      title: "송금 취소",
      render: (r) => action(r, "TRANSFER_CANCEL", onCancel, "송금 취소"),
    },
    {
      key: "retry",
      title: "재시도",
      render: (r) => action(r, "TRANSFER_RETRY", onRetryTransfer, "재시도"),
    },
  ];
  return (
    <>
      <PageHeader
        id="ADM-S09"
        hideBody
        title="송금 관리"
        description=""
        meta={refreshing ? <p aria-live="polite">최신 목록 확인 중</p> : undefined}
      />
      {notice ? (
        <Alert persistent variant="information" title="송금 목록 복귀">
          {notice}
        </Alert>
      ) : null}
      <div className="admin-funding-overview">
      <div className="admin-kpi-grid" aria-label="송금 요약">
        {[{label:"전체 건/금액",codes:[]},{label:"완료 건/금액",codes:["COMPLETED"]},{label:"대기 건/금액",codes:["READY"]},{label:"실패·취소 건/금액",codes:["FAILED","CANCELED"]},{label:"송금 중 건/금액",codes:["PROCESSING","UNKNOWN"]}].map(group => {
          const count = group.codes.length ? group.codes.reduce((n,k) => n + (result?.statusSummary?.[k]?.count ?? 0), 0) : result?.totalCount;
          const amount = group.codes.length ? group.codes.reduce((n,k) => n + (result?.statusSummary?.[k]?.amount ?? 0), 0) : result?.totalAmount;
          return <Kpi key={group.label} label={group.label} value={result ? `${count ?? 0}건 / ${money(amount)}` : "—"}/>;
        })}
      </div>
      {funding}
      </div>
      <Panel title="검색 조건">
        <form
          className="admin-filter-bar"
          noValidate
          onSubmit={form.handleSubmit((values) =>
            onSearch({
              ...values,
              keyword: values.keyword.trim(),
              accountNumber: values.accountNumber?.trim(),
            }),
          )}
        >
          <HookFormField
            compact
            formControl={form.control}
            label="시작일"
            name="from"
            type="date"
          />
          <HookFormField
            compact
            formControl={form.control}
            label="종료일"
            name="to"
            type="date"
          />
          <HookFormField
            compact
            control="select"
            formControl={form.control}
            label="송금 상태"
            name="status"
          >
            <option value="">전체</option>
            {[
              "COMPLETED",
              "READY",
              "FAILED",
              "CANCELED",
              "PROCESSING",
              "UNKNOWN",
            ].map((code) => (
              <option key={code} value={code}>
                {labels[code]}
              </option>
            ))}
          </HookFormField>
          <HookFormField
            compact
            formControl={form.control}
            label="거래번호·회원"
            name="keyword"
            maxLength={100}
          />
          <HookFormField
            compact
            formControl={form.control}
            label="입금 계좌번호"
            name="accountNumber"
            inputMode="numeric"
            maxLength={30}
          />
          <Button size="small" type="submit">
            조회
          </Button>
        </form>
      </Panel>
      <div className="admin-list-actions"><Button size="small" variant="secondary" disabled={!onExport} onClick={onExport}>다운로드</Button></div>
      <DataTable
        caption="송금 검색 결과"
        columns={columns}
        rows={state === "ready" ? (result?.items ?? []) : []}
        rowKey={(r) => r.id}
        state={state}
        onRetry={onRetry}
      />
      {result && state === "ready" ? (
        <Pagination
          page={result.page}
          size={result.size}
          totalCount={result.totalCount}
          onPageChange={onPageChange}
        />
      ) : null}
    </>
  );
}
